/**
 * Генератор TypeScript типов из JSON Schema
 * Создает TypeScript интерфейсы и типы на основе JSON Schema 7
 */

import { JSONSchema7, JSONSchema7Definition } from 'json-schema';

export interface TypeGenerationOptions {
  /** Префикс для генерируемых типов */
  typePrefix?: string;
  /** Суффикс для генерируемых типов */
  typeSuffix?: string;
  /** Использовать опциональные поля для необязательных свойств */
  optionalFields?: boolean;
  /** Генерировать readonly поля */
  readonly?: boolean;
  /** Генерировать JSDoc комментарии */
  includeJSDoc?: boolean;
  /** Экспортировать типы */
  exportTypes?: boolean;
  /** Использовать строгие типы */
  strict?: boolean;
  /** Генерировать enum'ы как union типы */
  enumAsUnion?: boolean;
}

export interface GeneratedType {
  /** Имя типа */
  name: string;
  /** Сгенерированный TypeScript код */
  code: string;
  /** Зависимости от других типов */
  dependencies: string[];
  /** Исходная JSON Schema */
  schema: JSONSchema7;
}

export interface TypeGenerationResult {
  /** Сгенерированные типы */
  types: GeneratedType[];
  /** Полный TypeScript код */
  code: string;
  /** Ошибки генерации */
  errors: string[];
  /** Предупреждения */
  warnings: string[];
}

/**
 * Генератор TypeScript типов из JSON Schema
 */
export class TypeScriptGenerator {
  private options: Required<TypeGenerationOptions>;
  private generatedTypes = new Map<string, GeneratedType>();
  private typeNames = new Set<string>();
  private errors: string[] = [];
  private warnings: string[] = [];

  constructor(options: TypeGenerationOptions = {}) {
    this.options = {
      typePrefix: options.typePrefix || '',
      typeSuffix: options.typeSuffix || '',
      optionalFields: options.optionalFields ?? true,
      readonly: options.readonly ?? false,
      includeJSDoc: options.includeJSDoc ?? true,
      exportTypes: options.exportTypes ?? true,
      strict: options.strict ?? true,
      enumAsUnion: options.enumAsUnion ?? true
    };
  }

  /**
   * Генерация TypeScript типов из JSON Schema
   */
  generateTypes(schemas: { [name: string]: JSONSchema7 }): TypeGenerationResult {
    this.reset();

    // Генерируем типы для каждой схемы
    for (const [name, schema] of Object.entries(schemas)) {
      try {
        this.generateType(name, schema);
      } catch (error) {
        this.errors.push(`Ошибка генерации типа ${name}: ${error instanceof Error ? error.message : String(error)}`);
      }
    }

    // Собираем итоговый код
    const code = this.buildFinalCode();

    return {
      types: Array.from(this.generatedTypes.values()),
      code,
      errors: this.errors,
      warnings: this.warnings
    };
  }

  /**
   * Генерация одного типа
   */
  private generateType(name: string, schema: JSONSchema7): GeneratedType {
    const typeName = this.formatTypeName(name);
    
    if (this.generatedTypes.has(typeName)) {
      return this.generatedTypes.get(typeName)!;
    }

    const dependencies: string[] = [];
    const typeCode = this.generateTypeDefinition(schema, typeName, dependencies);
    
    const generatedType: GeneratedType = {
      name: typeName,
      code: typeCode,
      dependencies,
      schema
    };

    this.generatedTypes.set(typeName, generatedType);
    this.typeNames.add(typeName);

    return generatedType;
  }

  /**
   * Генерация определения типа
   */
  private generateTypeDefinition(
    schema: JSONSchema7,
    typeName: string,
    dependencies: string[]
  ): string {
    let code = '';

    // JSDoc комментарий
    if (this.options.includeJSDoc) {
      code += this.generateJSDoc(schema);
    }

    // Экспорт
    const exportKeyword = this.options.exportTypes ? 'export ' : '';

    // Определение типа в зависимости от schema
    if (schema.type === 'object' || schema.properties) {
      code += `${exportKeyword}interface ${typeName} {\n`;
      code += this.generateObjectProperties(schema, dependencies);
      code += '}\n';
    } else if (schema.enum) {
      if (this.options.enumAsUnion) {
        const enumValues = schema.enum.map(value => 
          typeof value === 'string' ? `'${value}'` : String(value)
        ).join(' | ');
        code += `${exportKeyword}type ${typeName} = ${enumValues};\n`;
      } else {
        code += `${exportKeyword}enum ${typeName} {\n`;
        schema.enum.forEach((value, index) => {
          const enumKey = typeof value === 'string' ? 
            this.formatEnumKey(value) : `VALUE_${index}`;
          const enumValue = typeof value === 'string' ? `'${value}'` : String(value);
          code += `  ${enumKey} = ${enumValue},\n`;
        });
        code += '}\n';
      }
    } else {
      // Простой тип
      const tsType = this.jsonSchemaTypeToTypeScript(schema, dependencies);
      code += `${exportKeyword}type ${typeName} = ${tsType};\n`;
    }

    return code;
  }

  /**
   * Генерация свойств объекта
   */
  private generateObjectProperties(schema: JSONSchema7, dependencies: string[]): string {
    let code = '';
    
    if (!schema.properties) {
      return code;
    }

    const required = schema.required || [];

    for (const [propName, propSchema] of Object.entries(schema.properties)) {
      if (typeof propSchema === 'boolean') {
        continue; // Пропускаем boolean схемы
      }

      // JSDoc для свойства
      if (this.options.includeJSDoc && propSchema.description) {
        code += `  /** ${propSchema.description} */\n`;
      }

      // Определяем опциональность
      const isOptional = this.options.optionalFields && !required.includes(propName);
      const optional = isOptional ? '?' : '';
      
      // Readonly модификатор
      const readonlyModifier = this.options.readonly ? 'readonly ' : '';

      // Тип свойства
      const propType = this.jsonSchemaTypeToTypeScript(propSchema, dependencies);
      
      code += `  ${readonlyModifier}${propName}${optional}: ${propType};\n`;
    }

    return code;
  }

  /**
   * Преобразование JSON Schema типа в TypeScript тип
   */
  private jsonSchemaTypeToTypeScript(
    schema: JSONSchema7Definition,
    dependencies: string[]
  ): string {
    if (typeof schema === 'boolean') {
      return schema ? 'any' : 'never';
    }

    // Обработка ссылок
    if (schema.$ref) {
      const refName = this.extractRefName(schema.$ref);
      dependencies.push(refName);
      return refName;
    }

    // Обработка enum
    if (schema.enum) {
      return schema.enum.map(value => 
        typeof value === 'string' ? `'${value}'` : String(value)
      ).join(' | ');
    }

    // Обработка union типов
    if (schema.anyOf || schema.oneOf) {
      const unionSchemas = schema.anyOf || schema.oneOf || [];
      return unionSchemas
        .map(subSchema => this.jsonSchemaTypeToTypeScript(subSchema, dependencies))
        .join(' | ');
    }

    // Обработка allOf
    if (schema.allOf) {
      return schema.allOf
        .map(subSchema => this.jsonSchemaTypeToTypeScript(subSchema, dependencies))
        .join(' & ');
    }

    // Обработка массивов
    if (schema.type === 'array') {
      if (schema.items) {
        if (Array.isArray(schema.items)) {
          // Tuple тип
          const itemTypes = schema.items.map(item => this.jsonSchemaTypeToTypeScript(item, dependencies));
          return `[${itemTypes.join(', ')}]`;
        } else {
          const itemType = this.jsonSchemaTypeToTypeScript(schema.items, dependencies);
          return `${itemType}[]`;
        }
      }
      return 'any[]';
    }

    // Обработка объектов
    if (schema.type === 'object') {
      if (schema.properties) {
        // Inline объект
        let objectType = '{\n';
        const required = schema.required || [];
        
        for (const [propName, propSchema] of Object.entries(schema.properties)) {
          if (typeof propSchema === 'boolean') continue;
          
          const isOptional = this.options.optionalFields && !required.includes(propName);
          const optional = isOptional ? '?' : '';
          const propType = this.jsonSchemaTypeToTypeScript(propSchema, dependencies);
          
          objectType += `    ${propName}${optional}: ${propType};\n`;
        }
        
        objectType += '  }';
        return objectType;
      }
      
      if (schema.additionalProperties) {
        if (typeof schema.additionalProperties === 'boolean') {
          return schema.additionalProperties ? '{ [key: string]: any }' : '{}';
        }
        const valueType = this.jsonSchemaTypeToTypeScript(schema.additionalProperties, dependencies);
        return `{ [key: string]: ${valueType} }`;
      }
      
      return '{}';
    }

    // Базовые типы
    switch (schema.type) {
      case 'string':
        return 'string';
      case 'number':
        return 'number';
      case 'integer':
        return 'number';
      case 'boolean':
        return 'boolean';
      case 'null':
        return 'null';
      default:
        if (schema.type && Array.isArray(schema.type)) {
          return schema.type.map(t => this.primitiveTypeToTypeScript(t)).join(' | ');
        }
        return 'any';
    }
  }

  /**
   * Преобразование примитивного типа
   */
  private primitiveTypeToTypeScript(type: string): string {
    switch (type) {
      case 'string': return 'string';
      case 'number': return 'number';
      case 'integer': return 'number';
      case 'boolean': return 'boolean';
      case 'null': return 'null';
      case 'array': return 'any[]';
      case 'object': return 'object';
      default: return 'any';
    }
  }

  /**
   * Генерация JSDoc комментария
   */
  private generateJSDoc(schema: JSONSchema7): string {
    let comment = '/**\n';
    
    if (schema.title) {
      comment += ` * ${schema.title}\n`;
    }
    
    if (schema.description) {
      comment += ` * ${schema.description}\n`;
    }
    
    if (schema.examples && Array.isArray(schema.examples) && schema.examples.length > 0) {
      comment += ` * @example\n`;
      schema.examples.forEach((example: any) => {
        comment += ` * ${JSON.stringify(example)}\n`;
      });
    }
    
    comment += ' */\n';
    return comment;
  }

  /**
   * Форматирование имени типа
   */
  private formatTypeName(name: string): string {
    // Если имя уже в правильном формате, оставляем как есть
    if (/^[A-Z][a-zA-Z0-9]*$/.test(name)) {
      return `${this.options.typePrefix}${name}${this.options.typeSuffix}`;
    }
    
    // Преобразуем в PascalCase
    const pascalCase = name
      .replace(/[^a-zA-Z0-9]/g, '_')
      .split('_')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join('');
    
    return `${this.options.typePrefix}${pascalCase}${this.options.typeSuffix}`;
  }

  /**
   * Форматирование ключа enum
   */
  private formatEnumKey(value: string): string {
    return value
      .replace(/[^a-zA-Z0-9]/g, '_')
      .toUpperCase();
  }

  /**
   * Извлечение имени из $ref
   */
  private extractRefName(ref: string): string {
    const parts = ref.split('/');
    return this.formatTypeName(parts[parts.length - 1]);
  }

  /**
   * Сборка финального кода
   */
  private buildFinalCode(): string {
    let code = '';
    
    // Добавляем заголовок
    code += '// Автоматически сгенерированные TypeScript типы из JSON Schema\n';
    code += '// Не редактируйте этот файл вручную\n\n';
    
    // Добавляем импорты если нужно
    if (this.options.strict) {
      code += '/* eslint-disable */\n';
      code += '/* tslint:disable */\n\n';
    }

    // Добавляем все типы
    for (const generatedType of this.generatedTypes.values()) {
      code += generatedType.code + '\n';
    }

    return code;
  }

  /**
   * Сброс состояния генератора
   */
  private reset(): void {
    this.generatedTypes.clear();
    this.typeNames.clear();
    this.errors = [];
    this.warnings = [];
  }

  /**
   * Генерация типов из ValidationPlugin
   */
  static generateFromValidationPlugin(
    validationPlugin: any,
    options?: TypeGenerationOptions
  ): TypeGenerationResult {
    const generator = new TypeScriptGenerator(options);
    const schemas = validationPlugin.getAllJSONSchemas();
    
    // Преобразуем JSONSchemaValidation в JSONSchema7
    const jsonSchemas: { [name: string]: JSONSchema7 } = {};
    for (const [name, schemaValidation] of Object.entries(schemas)) {
      jsonSchemas[name] = (schemaValidation as any).schema;
    }
    
    return generator.generateTypes(jsonSchemas);
  }
}

/**
 * Утилитарная функция для быстрой генерации типов
 */
export function generateTypesFromSchemas(
  schemas: { [name: string]: JSONSchema7 },
  options?: TypeGenerationOptions
): TypeGenerationResult {
  const generator = new TypeScriptGenerator(options);
  return generator.generateTypes(schemas);
}

/**
 * Утилитарная функция для генерации типов в файл
 */
export async function generateTypesToFile(
  schemas: { [name: string]: JSONSchema7 },
  outputPath: string,
  options?: TypeGenerationOptions
): Promise<void> {
  const result = generateTypesFromSchemas(schemas, options);
  
  if (result.errors.length > 0) {
    throw new Error(`Ошибки генерации типов: ${result.errors.join(', ')}`);
  }

  // В реальном проекте здесь будет fs.writeFile
  // Пока что просто возвращаем результат
  console.log(`Типы сгенерированы в ${outputPath}:\n${result.code}`);
} 